import { SectionProps } from "../Section";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { useCalculationField, useDispatch } from "@ploy-lib/calculation";
import {
	Button,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	makeStyles,
	Typography
} from "@material-ui/core";
import clsx from "clsx";
import {
	AddCircleOutlineOutlined,
	DeleteOutline,
	CancelOutlined
} from "@material-ui/icons";
import React, { useCallback, useState } from "react";
import { TextField } from "@ploy-ui/form-fields";
import { Formik, Field as FormikField } from "formik";
import * as Yup from "yup";
import { ErrorDisplay } from "@ploy-lib/types";
import { defineMessages, useIntl } from "react-intl";
import { LabelComponent } from "../../field";

addRegisteredSectionLayout({
	name: "ProbateSettlementSection",
	displayName: "ProbateSettlementSection",
	settings: {
		editableOptions: {}
	}
});

const messages = defineMessages({
	captionTitle: {
		defaultMessage: "Who will you release?",
		id: "template-form.field.probateSettlements.captionTitle"
	},
	addButton: {
		defaultMessage: "Add",
		id: "template-form.field.probateSettlements.addButton"
	},
	addPerson: {
		defaultMessage: "Add new person",
		id: "template-form.field.probateSettlements.addPerson"
	},
	nameLabel: {
		defaultMessage: "Name",
		id: "template-form.field.probateSettlements.nameLabel"
	},
	validationMessage: {
		defaultMessage: "Field is required",
		id: "template-form.field.probateSettlements.validationMessage"
	},
	helperText: {
		defaultMessage: "We need the identity of the recipient",
		id: "template-form.field.probateSettlements.helperText"
	}
});

const useStyles = makeStyles(
	theme => ({
		root: {
			flexDirection: "column"
		},
		boxes: {
			border: "1px solid #757575",
			borderRadius: "4px",
			backgroundColor: "white"
		},
		list: {},
		listItem: { marginBottom: theme.spacing(1.5) },
		button: {
			padding: theme.spacing(1) + "px " + theme.spacing(2) + "px",
			justifyContent: "start"
		},
		icon: {
			height: theme.typography.button.lineHeight,
			width: theme.typography.button.lineHeight
		},
		addNames: {
			paddingTop: theme.spacing(6) + "px !important",
			paddingBottom: theme.spacing(4) + "px !important",
			backgroundColor: theme.palette.primary.light,
			borderColor: theme.palette.secondary.light,
			borderWidth: 1,
			borderTopStyle: "solid",
			borderBottomStyle: "solid",
			rowGap: theme.spacing(2)
		}
	}),
	{ name: "ProbateSettlementSection" }
);

const calcRulesNamespace = "Calculator";
const calcRulesName = "PROBATE_RECIPIENTS";

const ProbateSettlementSection = (props: SectionProps) => {
	const styles = useStyles(props);
	const intl = useIntl();
	const [addNewPerson, setAddNewPerson] = React.useState(false);
	const dispatch = useDispatch();
	const probateField = useCalculationField<string[] | undefined>(
		calcRulesNamespace,
		calcRulesName
	);

	const addItem: (newItem: string) => void = useCallback(
		(newItem: string) => {
			let probateList = probateField.value;
			probateList ??= [];
			probateList.push(newItem);

			dispatch({
				type: "patch",
				payload: {
					patches: [
						{
							target: calcRulesName,
							namespace: calcRulesNamespace,
							value: probateList,
							overwrite: true
						}
					]
				}
			});
		},
		[dispatch, probateField]
	);

	const deleteItem: (indexToRemove: number) => void = useCallback(
		(indexToRemove: number) => {
			let probateList = probateField.value!;
			probateList = probateList.filter((_, i) => i !== indexToRemove);

			dispatch({
				type: "patch",
				payload: {
					patches: [
						{
							target: calcRulesName,
							namespace: calcRulesNamespace,
							value: probateList,
							overwrite: true
						}
					]
				}
			});
		},
		[dispatch, probateField.value]
	);

	const [showHelperText, setShowHelperText] = useState(false);
	const toggleHelperText = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			setShowHelperText(show => !show);
			e.stopPropagation();
		},
		[]
	);

	const probateSettlementValidationSchema: Yup.ObjectSchema<{ name: string }> =
		Yup.object({
			name: Yup.string().required(
				intl.formatMessage(messages.validationMessage)
			)
		});

	return (
		<Grid
			container
			item
			spacing={1}
			className={clsx(styles.root, props.className)}
		>
			<Grid item xs={12}>
				<LabelComponent
					labelText={
						<Typography variant="caption">
							{intl.formatMessage(messages.captionTitle)}
						</Typography>
					}
					trimmedHelperText={intl.formatMessage(messages.helperText)}
					showHelperText={showHelperText}
					toggleHelperText={toggleHelperText}
				/>
			</Grid>
			{(probateField.value?.length || 0) > 0 && (
				<Grid item xs={12}>
					<List className={clsx(styles.list)}>
						{probateField.value?.map((name, i) => (
							<ListItem
								className={clsx(styles.listItem, styles.boxes)}
								key={name + i.toString()}
							>
								<ListItemText primary={name}></ListItemText>
								<ListItemSecondaryAction>
									<IconButton
										onClick={() => {
											deleteItem(i);
										}}
									>
										<DeleteOutline />
									</IconButton>
								</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>
				</Grid>
			)}

			{addNewPerson ? (
				<Grid item xs={12}>
					<Formik
						validationSchema={probateSettlementValidationSchema}
						onSubmit={(newEntry: { name: string }, _: any) => {
							addItem(newEntry.name);
						}}
						initialValues={{ name: "" }}
					>
						{formikProps => (
							<Grid container item className={clsx(styles.addNames)}>
								<FormikField
									name="name"
									component={TextField}
									errorDisplay={ErrorDisplay.Touched}
									fullWidth
									label={intl.formatMessage(messages.nameLabel)}
									style={{ padding: 4 }}
								/>
								<Grid
									item
									container
									direction="row"
									justifyContent="space-between"
								>
									<Button
										variant="contained"
										onClick={() => {
											setAddNewPerson(false);
											formikProps.submitForm();
										}}
										disabled={
											!formikProps.isValid ||
											formikProps.values.name.length === 0
										}
									>
										{intl.formatMessage(messages.addButton)}
									</Button>
									<IconButton>
										<CancelOutlined
											className={styles.icon}
											onClick={() => setAddNewPerson(false)}
										/>
									</IconButton>
								</Grid>
							</Grid>
						)}
					</Formik>
				</Grid>
			) : (
				<Grid item xs={12}>
					<Button
						fullWidth
						variant="outlined"
						startIcon={<AddCircleOutlineOutlined className={styles.icon} />}
						className={clsx(styles.button, styles.boxes)}
						onClick={() => {
							setAddNewPerson(true);
						}}
					>
						{intl.formatMessage(messages.addPerson)}
					</Button>
				</Grid>
			)}
		</Grid>
	);
};

ProbateSettlementSection.displayName = "ProbateSettlementSection";

export { ProbateSettlementSection };
